import {
  Component, Input, Output, EventEmitter, ElementRef, ViewChild,
  AfterViewInit, OnChanges, OnDestroy, SimpleChanges
} from '@angular/core';
import * as atlas from 'azure-maps-control';

export interface ICompetitorMapPinInput {
  id: number | string;
  name: string;
  latitude: string | number;
  longitude: string | number;
  rate?: number | null;
  distanceMiles?: number | null;
}

/**
 * Story 4.3/5.1/5.2 (AD-11): the one shared Azure Maps wrapper for both the Competitor Rate
 * Dashboard's Market Map and Add/Change Unit's Map-icon popover — presentational only, no HTTP
 * calls of its own. Callers supply the own-facility location and competitor pins via @Input().
 */
@Component({
  selector: 'app-competitor-market-map',
  templateUrl: './competitor-market-map.component.html',
  styleUrls: ['./competitor-market-map.component.scss'],
  standalone: false
})
export class CompetitorMarketMapComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() ownName: string;
  @Input() ownLatitude: string | number;
  @Input() ownLongitude: string | number;
  @Input() pins: ICompetitorMapPinInput[] = [];
  @Input() height = '300px';
  @Output() pinClicked = new EventEmitter<ICompetitorMapPinInput>();

  @ViewChild('mapContainer', { static: true }) mapContainer: ElementRef<HTMLDivElement>;

  private map: atlas.Map;
  private popup: atlas.Popup;
  private ownMarker: atlas.HtmlMarker;
  private pinMarkers: atlas.HtmlMarker[] = [];
  private mapReady = false;
  private readonly colors = ['#d93636', '#eab308', '#14b8a6', '#8b5cf6', '#ec4899', '#3b82f6'];

  ngAfterViewInit(): void {
    this.map = new atlas.Map(this.mapContainer.nativeElement, {
      style: 'road',
      showLogo: false,
      showFeedbackLink: false
    });

    this.popup = new atlas.Popup({ pixelOffset: [0, -32] });

    this.map.events.add('ready', () => {
      this.mapReady = true;
      this.render();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.mapReady && (changes.pins || changes.ownLatitude || changes.ownLongitude || changes.ownName)) {
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.map?.dispose();
  }

  // Resizing after the container becomes visible (e.g. inside a just-opened modal) — Azure Maps
  // does not auto-detect a container that was hidden (display:none) at construction time.
  resize(): void {
    this.map?.resize('100%', '100%');
    if (this.mapReady) {
      this.render();
    }
  }

  private clearMarkers(): void {
    if (this.ownMarker) { this.map.markers.remove(this.ownMarker); this.ownMarker = undefined; }
    this.pinMarkers.forEach(m => this.map.markers.remove(m));
    this.pinMarkers = [];
  }

  private render(): void {
    if (!this.map) {
      return;
    }

    this.clearMarkers();

    const ownLat = parseFloat(this.ownLatitude as string);
    const ownLon = parseFloat(this.ownLongitude as string);
    const hasOwnLocation = !isNaN(ownLat) && !isNaN(ownLon);

    if (hasOwnLocation) {
      this.ownMarker = new atlas.HtmlMarker({
        position: [ownLon, ownLat],
        anchor: 'center',
        htmlContent: this.ownMarkerHtml(this.ownName)
      });
      this.map.markers.add(this.ownMarker);
      this.map.events.add('mouseover', this.ownMarker, () => this.showPopup(this.ownMarker, this.ownName, null, null));
      this.map.events.add('mouseout', this.ownMarker, () => this.popup.close());
    }

    let firstPinPosition: atlas.data.Position;

    (this.pins || []).forEach((pin, i) => {
      const lat = parseFloat(pin.latitude as string);
      const lon = parseFloat(pin.longitude as string);
      if (isNaN(lat) || isNaN(lon)) {
        return;
      }

      if (!firstPinPosition) {
        firstPinPosition = [lon, lat];
      }

      const color = this.colors[i % this.colors.length];
      const marker = new atlas.HtmlMarker({
        position: [lon, lat],
        anchor: 'center',
        htmlContent: this.pinMarkerHtml(this.getInitials(pin.name), color)
      });
      this.map.markers.add(marker);
      this.map.events.add('mouseover', marker, () => {
        this.showPopup(marker, pin.name, pin.rate, pin.distanceMiles ?? null);
        this.pinClicked.emit(pin);
      });
      this.map.events.add('mouseout', marker, () => this.popup.close());
      this.pinMarkers.push(marker);
    });

    const cameraCenter = hasOwnLocation ? ([ownLon, ownLat] as atlas.data.Position) : firstPinPosition;
    if (cameraCenter) {
      this.map.setCamera({ center: cameraCenter, zoom: 12, type: 'jump' });
    }
  }

  // AC #4: rate/distance popup shown on hover (mouseover/mouseout), not click.
  private showPopup(marker: atlas.HtmlMarker, name: string, rate: number | null | undefined, distanceMiles: number | null): void {
    const rateLine = rate != null ? `<div>$${rate}/mo</div>` : '';
    const distanceLine = distanceMiles != null ? `<div>${distanceMiles} mi</div>` : '';

    this.popup.setOptions({
      content: `<div class="map-popup"><strong>${this.escapeHtml(name)}</strong>${rateLine}${distanceLine}</div>`,
      position: marker.getOptions().position
    });
    this.popup.open(this.map);
  }

  private getInitials(name: string): string {
    return (name || '')
      .split(' ')
      .map(w => w[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }

  // Marker/popup content is set via Azure Maps' htmlContent (raw HTML) API — StorTrack/user-entered
  // names are escaped here before interpolation to avoid HTML/script injection into the map.
  private escapeHtml(value: string): string {
    const div = document.createElement('div');
    div.textContent = value ?? '';
    return div.innerHTML;
  }

  private ownMarkerHtml(name: string): string {
    return `
    <div style="position:relative; width:32px; height:40px; cursor:pointer;">
      <div style="position:absolute; top:0; left:50%; width:28px; height:28px; transform:translateX(-50%) rotate(-45deg);
        background:#2864F0; border-radius:50% 50% 50% 0; box-shadow:0 2px 6px rgba(0,0,0,0.25);">
        <div style="position:absolute; top:50%; left:50%; width:10px; height:10px;
          transform:translate(-50%,-50%) rotate(45deg); background:#fff; border-radius:50%;"></div>
      </div>
    </div>
  `;
  }

  private pinMarkerHtml(initials: string, color: string): string {
    return `
    <div style="position:relative; width:28px; height:36px; cursor:pointer;">
      <div style="position:absolute; top:0; left:50%; width:24px; height:24px; transform:translateX(-50%) rotate(-45deg);
        background:${color}; border-radius:50% 50% 50% 0; box-shadow:0 2px 6px rgba(0,0,0,0.25);">
        <div style="position:absolute; top:50%; left:50%; width:7px; height:7px; transform:translate(-50%,-50%) rotate(45deg); background:#fff; border-radius:50%;"></div>
      </div>
    </div>
  `;
  }
}
